<script setup>
/**
 * Painel “Editar Contato” — espelho do ContatoPanel TiliT
 * (Dados / Personalizados / Tags / Arquivos / Tarefas / Notas / Interações / Timeline).
 */
import { computed, reactive, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { formatarTelefone } from './crmMap';
import {
  apiCrmCampos,
  apiCrmContatoDelete,
  apiCrmContatoGet,
  apiCrmContatoMover,
  apiCrmContatoPatch,
  apiCrmTagCreate,
  apiCrmTags,
  apiCrmUsuarios,
} from './crmApi';

const props = defineProps({
  contatoId: { type: String, required: true },
  colunaId: { type: String, required: true },
  colunas: { type: Array, default: () => [] },
  resumo: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved', 'removed', 'avisar']);

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const ABAS = [
  { id: 'dados', label: 'Dados' },
  { id: 'personalizados', label: 'Dados personalizados' },
  { id: 'tags', label: 'Tags' },
  // Arquivos / Tarefas / Notas / Interações / Timeline — ocultos (não usados)
];

const aba = ref('dados');
const loading = ref(true);
const saving = ref(false);
const contato = ref(null);
const catalogoTags = ref([]);
const catalogoCampos = ref([]);
const usuarios = ref([]);
const tagBusca = ref('');
const tagNova = ref('');
const zoom = ref(1);

const form = reactive(emptyForm());

function emptyForm() {
  return {
    nome: '',
    telefone: '',
    ddi: '+55',
    colunaId: '',
    email: '',
    origem: '',
    dataNascimento: '',
    valorOportunidade: '',
    anotacoes: '',
    responsavel: '',
    responsavelUsuarioId: null,
    automacaoAtiva: true,
    tags: [],
    camposPersonalizados: {},
    notas: [],
    tarefas: [],
    interacoes: [],
    arquivos: [],
    timeline: [],
  };
}

function uid(prefix) {
  return `${prefix}-${Math.random().toString(16).slice(2, 10)}`;
}

function applyContato(c) {
  contato.value = c;
  Object.assign(form, {
    nome: c.nome || '',
    telefone: String(c.telefone || '').replace(/\D/g, ''),
    ddi: c.ddi || '+55',
    colunaId: props.colunaId || c.colunaId || '',
    email: c.email || '',
    origem: c.origem || '',
    dataNascimento: c.dataNascimento || '',
    valorOportunidade: c.valorOportunidade || '',
    anotacoes: c.anotacoes || c.nota || '',
    responsavel: c.responsavel || '',
    responsavelUsuarioId: c.responsavelUsuarioId ?? null,
    automacaoAtiva: c.automacaoAtiva !== false,
    tags: Array.isArray(c.tags) ? c.tags.slice() : [],
    camposPersonalizados: { ...(c.camposPersonalizados || {}) },
    notas: Array.isArray(c.notas) ? c.notas.slice() : [],
    tarefas: Array.isArray(c.tarefas) ? c.tarefas.slice() : [],
    interacoes: Array.isArray(c.interacoes) ? c.interacoes.slice() : [],
    arquivos: Array.isArray(c.arquivos) ? c.arquivos.slice() : [],
    timeline: Array.isArray(c.timeline) ? c.timeline.slice() : [],
  });
}

async function carregar() {
  loading.value = true;
  aba.value = 'dados';
  if (props.resumo) {
    applyContato({
      ...props.resumo,
      anotacoes: props.resumo.nota || props.resumo.anotacoes || '',
    });
  }
  const acc = Number(accountId.value);
  const user = currentUser.value;
  try {
    const [full, tagsR, camposR, usersR] = await Promise.all([
      apiCrmContatoGet(acc, user, props.contatoId),
      apiCrmTags(acc, user).catch(() => ({ tags: [] })),
      apiCrmCampos(acc, user).catch(() => ({ campos: [] })),
      apiCrmUsuarios(acc, user).catch(() => ({ usuarios: [] })),
    ]);
    catalogoTags.value = tagsR.tags || [];
    catalogoCampos.value = (camposR.campos || []).filter(c => c?.ativo !== false);
    usuarios.value = usersR.usuarios || [];
    const c = full.contato || full;
    if (c?.id) applyContato(c);
  } catch (e) {
    emit('avisar', e.message || 'Falha ao carregar contato');
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.contatoId,
  () => {
    if (props.contatoId) carregar();
  },
  { immediate: true }
);

const tagsFiltradas = computed(() => {
  const q = tagBusca.value.trim().toLowerCase();
  return (form.tags || []).filter(t => !q || String(t).toLowerCase().includes(q));
});

const tagsSugestoes = computed(() => {
  const q = tagBusca.value.trim().toLowerCase();
  const atuais = new Set((form.tags || []).map(t => String(t).toLowerCase()));
  return (catalogoTags.value || [])
    .filter(t => t?.ativo !== false)
    .filter(t => !atuais.has(String(t.nome || '').toLowerCase()))
    .filter(t => !q || String(t.nome || '').toLowerCase().includes(q))
    .slice(0, 24);
});

const camposAtivos = computed(() =>
  [...(catalogoCampos.value || [])].sort((a, b) =>
    String(a.nome || '').localeCompare(String(b.nome || ''))
  )
);

const extrasPersonalizados = computed(() => {
  const nomes = new Set(camposAtivos.value.map(c => c.nome));
  return Object.entries(form.camposPersonalizados || {}).filter(([k]) => !nomes.has(k));
});

const timelineFiltro = ref('todos');
const TL_FILTROS = [
  { id: 'todos', label: 'Todos' },
  { id: 'lead', label: 'Lead' },
  { id: 'tarefa', label: 'Tarefa' },
  { id: 'nota', label: 'Nota' },
  { id: 'interacao', label: 'Interação' },
  { id: 'arquivo', label: 'Arquivo' },
  { id: 'kanban', label: 'Kanban' },
];
const timelineFiltrada = computed(() => {
  const list = form.timeline || [];
  if (timelineFiltro.value === 'todos') return list.slice().reverse();
  return list.filter(t => t.tipo === timelineFiltro.value).reverse();
});

function setResponsavel(idStr) {
  if (!idStr) {
    form.responsavel = '';
    form.responsavelUsuarioId = null;
    return;
  }
  const id = Number(idStr);
  const u = usuarios.value.find(x => Number(x.id) === id);
  form.responsavelUsuarioId = id;
  form.responsavel = u?.nome || '';
}

async function patch(body) {
  const acc = Number(accountId.value);
  const user = currentUser.value;
  const r = await apiCrmContatoPatch(acc, user, props.contatoId, body);
  const c = r.contato || r;
  if (c?.id) applyContato(c);
  return c;
}

async function salvarTudo() {
  saving.value = true;
  try {
    await patch({
      nome: form.nome.trim(),
      telefone: form.telefone.replace(/\D/g, ''),
      ddi: form.ddi,
      email: form.email,
      origem: form.origem,
      dataNascimento: form.dataNascimento,
      valorOportunidade: form.valorOportunidade,
      anotacoes: form.anotacoes,
      responsavel: form.responsavel,
      responsavelUsuarioId: form.responsavelUsuarioId,
      automacaoAtiva: form.automacaoAtiva,
      tags: form.tags,
      camposPersonalizados: form.camposPersonalizados,
      notas: form.notas,
      tarefas: form.tarefas,
      interacoes: form.interacoes,
      timeline: form.timeline,
    });
    if (form.colunaId && form.colunaId !== props.colunaId) {
      await apiCrmContatoMover(
        Number(accountId.value),
        currentUser.value,
        props.contatoId,
        form.colunaId
      );
    }
    emit('avisar', 'Contato atualizado');
    emit('saved');
    emit('close');
  } catch (e) {
    emit('avisar', e.message || 'Falha ao salvar');
  } finally {
    saving.value = false;
  }
}

async function excluir() {
  if (!window.confirm('Excluir este contato?')) return;
  try {
    await apiCrmContatoDelete(
      Number(accountId.value),
      currentUser.value,
      props.contatoId
    );
    emit('removed', props.contatoId);
    emit('close');
  } catch (e) {
    emit('avisar', e.message || 'Falha ao excluir');
  }
}

async function vincularTag(nome) {
  const t = String(nome || '').trim();
  if (!t || form.tags.includes(t)) return;
  const next = [...form.tags, t];
  try {
    await patch({ tags: next });
  } catch (e) {
    emit('avisar', e.message);
  }
}

async function removerTag(nome) {
  try {
    await patch({ tags: form.tags.filter(t => t !== nome) });
  } catch (e) {
    emit('avisar', e.message);
  }
}

async function adicionarTag() {
  const t = tagNova.value.trim();
  if (!t) return;
  const noCat = catalogoTags.value.find(
    x => String(x.nome || '').toLowerCase() === t.toLowerCase()
  );
  try {
    if (noCat) await vincularTag(noCat.nome);
    else {
      const criada = await apiCrmTagCreate(
        Number(accountId.value),
        currentUser.value,
        t
      );
      const tag = criada.tag || criada;
      if (tag?.nome) catalogoTags.value = [...catalogoTags.value, tag];
      await vincularTag(tag?.nome || t);
    }
    tagNova.value = '';
  } catch (e) {
    emit('avisar', e.message);
  }
}

function setCampoPers(nome, valor) {
  form.camposPersonalizados = { ...form.camposPersonalizados, [nome]: valor };
}

const extraChave = ref('');
const extraValor = ref('');
function addExtra() {
  const k = extraChave.value.trim();
  if (!k) return;
  setCampoPers(k, extraValor.value);
  extraChave.value = '';
  extraValor.value = '';
}

/* ---- Notas ---- */
const notaAberto = ref(false);
const notaTexto = ref('');
const notaEditId = ref(null);
function abrirNota() {
  notaEditId.value = null;
  notaTexto.value = '';
  notaAberto.value = true;
}
async function salvarNota() {
  if (!notaTexto.value.trim()) return;
  const agora = new Date().toISOString();
  let notas;
  let timeline = form.timeline.slice();
  if (notaEditId.value) {
    notas = form.notas.map(n =>
      n.id === notaEditId.value ? { ...n, texto: notaTexto.value.trim() } : n
    );
  } else {
    const nota = {
      id: uid('nota'),
      texto: notaTexto.value.trim(),
      autor: currentUser.value?.available_name || 'Você',
      email: currentUser.value?.email || '',
      criadoEm: agora,
    };
    notas = [nota, ...form.notas];
    timeline = [
      {
        id: uid('tl'),
        tipo: 'nota',
        titulo: 'Nota Interna',
        detalhe: `Comentário: ${nota.texto.slice(0, 80)}`,
        em: agora,
      },
      ...timeline,
    ];
  }
  try {
    await patch({ notas, timeline });
    notaAberto.value = false;
    notaTexto.value = '';
    notaEditId.value = null;
  } catch (e) {
    emit('avisar', e.message);
  }
}
async function excluirNota(id) {
  if (!window.confirm('Excluir esta nota?')) return;
  try {
    await patch({ notas: form.notas.filter(n => n.id !== id) });
  } catch (e) {
    emit('avisar', e.message);
  }
}

/* ---- Tarefas ---- */
const tarAberto = ref(false);
const tarEditId = ref(null);
const tarForm = reactive({
  titulo: '',
  vencimento: '',
  status: 'pendente',
  descricao: '',
  responsavel: '',
});
function abrirTarefa() {
  tarEditId.value = null;
  Object.assign(tarForm, {
    titulo: '',
    vencimento: '',
    status: 'pendente',
    descricao: '',
    responsavel: '',
  });
  tarAberto.value = true;
}
function editarTarefa(t) {
  tarEditId.value = t.id;
  Object.assign(tarForm, {
    titulo: t.titulo,
    vencimento: t.vencimento,
    status: t.status,
    descricao: t.descricao || '',
    responsavel: t.responsavel || '',
  });
  tarAberto.value = true;
}
async function salvarTarefa() {
  if (!tarForm.titulo.trim() || !tarForm.vencimento) return;
  const agora = new Date().toISOString();
  let tarefas;
  let timeline = form.timeline.slice();
  if (tarEditId.value) {
    tarefas = form.tarefas.map(t =>
      t.id === tarEditId.value
        ? {
            ...t,
            titulo: tarForm.titulo.trim(),
            vencimento: tarForm.vencimento,
            status: tarForm.status,
            descricao: tarForm.descricao,
            responsavel: tarForm.responsavel,
          }
        : t
    );
  } else {
    const t = {
      id: uid('tar'),
      titulo: tarForm.titulo.trim(),
      vencimento: tarForm.vencimento,
      status: tarForm.status,
      descricao: tarForm.descricao,
      responsavel: tarForm.responsavel,
    };
    tarefas = [t, ...form.tarefas];
    timeline = [
      {
        id: uid('tl'),
        tipo: 'tarefa',
        titulo: 'Tarefa',
        detalhe: t.titulo,
        em: agora,
      },
      ...timeline,
    ];
  }
  try {
    await patch({ tarefas, timeline });
    tarAberto.value = false;
  } catch (e) {
    emit('avisar', e.message);
  }
}
async function excluirTarefa(id) {
  if (!window.confirm('Excluir esta tarefa?')) return;
  try {
    await patch({ tarefas: form.tarefas.filter(t => t.id !== id) });
  } catch (e) {
    emit('avisar', e.message);
  }
}

/* ---- Interações ---- */
const intAberto = ref(false);
const intEditId = ref(null);
const intForm = reactive({
  responsavel: '',
  data: '',
  hh: '',
  mm: '',
  descricao: '',
});
function abrirInteracao() {
  intEditId.value = null;
  Object.assign(intForm, { responsavel: '', data: '', hh: '', mm: '', descricao: '' });
  intAberto.value = true;
}
function editarInteracao(i) {
  const [hh = '', mm = ''] = String(i.hora || '').split(':');
  intEditId.value = i.id;
  Object.assign(intForm, {
    responsavel: i.responsavel || '',
    data: i.data || '',
    hh: hh.slice(0, 2),
    mm: mm.slice(0, 2),
    descricao: i.descricao || '',
  });
  intAberto.value = true;
}
async function salvarInteracao() {
  if (!intForm.descricao.trim() || !intForm.data) return;
  const hora = `${intForm.hh.padStart(2, '0')}:${intForm.mm.padStart(2, '0')}`;
  const dados = {
    descricao: intForm.descricao.trim(),
    data: intForm.data,
    hora,
    responsavel: intForm.responsavel,
  };
  const agora = new Date().toISOString();
  let interacoes;
  let timeline = form.timeline.slice();
  if (intEditId.value) {
    interacoes = form.interacoes.map(i =>
      i.id === intEditId.value ? { ...i, ...dados } : i
    );
  } else {
    const item = { id: uid('int'), ...dados };
    interacoes = [item, ...form.interacoes];
    timeline = [
      {
        id: uid('tl'),
        tipo: 'interacao',
        titulo: 'Nova interação',
        detalhe: item.descricao.slice(0, 120),
        em: agora,
      },
      ...timeline,
    ];
  }
  try {
    await patch({ interacoes, timeline });
    intAberto.value = false;
  } catch (e) {
    emit('avisar', e.message);
  }
}
async function excluirInteracao(id) {
  if (!window.confirm('Excluir esta interação?')) return;
  try {
    await patch({ interacoes: form.interacoes.filter(i => i.id !== id) });
  } catch (e) {
    emit('avisar', e.message);
  }
}

function formatBytes(n) {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtQuando(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
}

const tituloSub = computed(
  () => form.nome || props.resumo?.nome || 'Contato'
);
</script>

<template>
  <aside class="contato-panel" role="dialog" aria-label="Editar Contato" :style="{ transform: `scale(${zoom})` }">
    <header class="contato-header">
      <div class="contato-header-text">
        <h2>Editar Contato</h2>
        <p class="contato-sub">{{ tituloSub }}</p>
      </div>
      <div class="contato-header-tools">
        <button type="button" class="tf-btn ghost sm" title="Diminuir" @click="zoom = Math.max(0.8, +(zoom - 0.1).toFixed(2))">−</button>
        <span class="zoom-lbl">{{ Math.round(zoom * 100) }}%</span>
        <button type="button" class="tf-btn ghost sm" title="Aumentar" @click="zoom = Math.min(1.3, +(zoom + 0.1).toFixed(2))">+</button>
        <button type="button" class="tf-btn ghost icon sm" aria-label="Fechar" @click="emit('close')">
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </header>

    <nav class="contato-tabs" aria-label="Seções do contato">
      <button
        v-for="t in ABAS"
        :key="t.id"
        type="button"
        class="contato-tab"
        :class="{ 'is-active': aba === t.id }"
        @click="aba = t.id"
      >
        {{ t.label }}
      </button>
    </nav>

    <div class="contato-body">
      <p v-if="loading" class="tf-hint">Carregando…</p>

      <!-- DADOS -->
      <div v-else-if="aba === 'dados'" class="aba-form">
        <label class="field">
          <span>Nome *</span>
          <input v-model="form.nome" class="input" placeholder="Nome do contato" />
        </label>
        <label class="field">
          <span>Telefone *</span>
          <div class="phone-row">
            <select v-model="form.ddi" class="input ddi" aria-label="DDI">
              <option value="+55">🇧🇷 +55</option>
              <option value="+1">🇺🇸 +1</option>
              <option value="+351">🇵🇹 +351</option>
              <option value="+34">🇪🇸 +34</option>
              <option value="+54">🇦🇷 +54</option>
            </select>
            <input v-model="form.telefone" class="input" placeholder="Digite o telefone" />
          </div>
        </label>
        <label class="field">
          <span>Etapa</span>
          <select v-model="form.colunaId" class="input">
            <option v-for="c in colunas" :key="c.id" :value="c.id">{{ c.titulo }}</option>
          </select>
        </label>
        <label class="field">
          <span>Responsável</span>
          <select
            class="input"
            :value="form.responsavelUsuarioId != null ? String(form.responsavelUsuarioId) : ''"
            @change="setResponsavel($event.target.value)"
          >
            <option value="">Sem responsável</option>
            <option v-for="u in usuarios" :key="u.id" :value="String(u.id)">{{ u.nome }}</option>
          </select>
          <p class="field-hint">Espelha o atendente atribuído na conversa do Atendimento</p>
        </label>
        <label class="field">
          <span>Email</span>
          <input v-model="form.email" class="input" placeholder="email@exemplo.com" />
        </label>
        <label class="field">
          <span>Origem</span>
          <input v-model="form.origem" class="input" placeholder="Ex: Site, Indicação, WhatsApp..." />
        </label>
        <label class="field">
          <span>Data de Nascimento</span>
          <input v-model="form.dataNascimento" class="input" type="date" />
        </label>
        <label class="field">
          <span>Valor da Oportunidade</span>
          <input v-model="form.valorOportunidade" class="input" placeholder="R$ 0,00" />
        </label>
        <label class="field">
          <span>Anotações</span>
          <textarea v-model="form.anotacoes" class="input textarea" rows="4" placeholder="Anotações sobre o contato..." />
        </label>
        <div class="campo-switch-row">
          <div>
            <label>Atendimento pela IA</label>
            <p class="field-hint">Mesmo controle do Assistente neste telefone</p>
          </div>
          <button
            type="button"
            role="switch"
            class="campo-switch"
            :class="{ 'is-on': form.automacaoAtiva }"
            :aria-checked="form.automacaoAtiva"
            @click="form.automacaoAtiva = !form.automacaoAtiva"
          >
            <span class="campo-switch-knob" />
          </button>
        </div>
      </div>

      <!-- PERSONALIZADOS -->
      <div v-else-if="aba === 'personalizados'" class="aba-form">
        <template v-if="camposAtivos.length">
          <label v-for="c in camposAtivos" :key="c.id || c.nome" class="field">
            <span>{{ c.nome }}</span>
            <select
              v-if="c.tipo === 'lista' && (c.opcoes || []).length"
              class="input"
              :value="form.camposPersonalizados[c.nome] || ''"
              @change="setCampoPers(c.nome, $event.target.value)"
            >
              <option value="">Selecione</option>
              <option v-for="o in c.opcoes" :key="o" :value="o">{{ o }}</option>
            </select>
            <button
              v-else-if="c.tipo === 'booleano'"
              type="button"
              role="switch"
              class="campo-switch"
              :class="{
                'is-on':
                  form.camposPersonalizados[c.nome] === 'true' ||
                  form.camposPersonalizados[c.nome] === '1',
              }"
              @click="
                setCampoPers(
                  c.nome,
                  form.camposPersonalizados[c.nome] === 'true' ||
                    form.camposPersonalizados[c.nome] === '1'
                    ? 'false'
                    : 'true'
                )
              "
            >
              <span class="campo-switch-knob" />
            </button>
            <input
              v-else
              class="input"
              :type="c.tipo === 'numero' ? 'number' : c.tipo === 'data' ? 'date' : 'text'"
              :value="form.camposPersonalizados[c.nome] || ''"
              :placeholder="c.descricao || c.nome"
              @input="setCampoPers(c.nome, $event.target.value)"
            />
          </label>
        </template>
        <div v-for="([k, v]) in extrasPersonalizados" :key="k" class="field">
          <span>{{ k }}</span>
          <input class="input" :value="v" @input="setCampoPers(k, $event.target.value)" />
        </div>
        <div class="inline-add">
          <input v-model="extraChave" class="input" placeholder="Campo livre" />
          <input v-model="extraValor" class="input" placeholder="Valor" />
          <button type="button" class="tf-btn primary sm" @click="addExtra">Add</button>
        </div>
        <p v-if="!camposAtivos.length && !extrasPersonalizados.length" class="empty-hint">
          Nenhum campo personalizado. Adicione um campo livre acima.
        </p>
      </div>

      <!-- TAGS -->
      <div v-else-if="aba === 'tags'" class="aba-form">
        <input v-model="tagBusca" class="input" placeholder="Buscar tags..." />
        <div v-if="tagsSugestoes.length" class="tag-suggestions">
          <span class="empty-hint">Sugestões do catálogo</span>
          <div class="tag-list">
            <button
              v-for="t in tagsSugestoes"
              :key="t.id || t.nome"
              type="button"
              class="tag-chip is-add"
              @click="vincularTag(t.nome)"
            >
              + {{ t.nome }}
            </button>
          </div>
        </div>
        <div v-if="!form.tags.length" class="empty-block">
          <p>Nenhuma tag</p>
          <p class="empty-hint">Nenhuma tag associada a este contato</p>
        </div>
        <div v-else class="tag-list">
          <button
            v-for="t in tagsFiltradas"
            :key="t"
            type="button"
            class="tag-chip"
            @click="removerTag(t)"
          >
            {{ t }} ×
          </button>
        </div>
        <div class="inline-add">
          <input
            v-model="tagNova"
            class="input"
            placeholder="Adicionar tag"
            @keydown.enter.prevent="adicionarTag"
          />
          <button type="button" class="tf-btn primary sm" @click="adicionarTag">
            Adicionar tag
          </button>
        </div>
      </div>

      <!-- ARQUIVOS -->
      <div v-else-if="aba === 'arquivos'" class="aba-form">
        <p class="empty-hint">
          Upload de arquivos pelo painel 179 em breve — listagem abaixo vem do CRM TiliT.
        </p>
        <div v-if="!form.arquivos.length" class="empty-block"><p>Nenhum arquivo</p></div>
        <ul v-else class="file-list">
          <li v-for="a in form.arquivos" :key="a.id">
            <div>
              <strong>{{ a.nome }}</strong>
              <small v-if="a.tamanho">{{ formatBytes(a.tamanho) }}</small>
            </div>
          </li>
        </ul>
      </div>

      <!-- TAREFAS -->
      <div v-else-if="aba === 'tarefas'" class="aba-tab">
        <div class="aba-cab">
          <span>{{ form.tarefas.length }} tarefa(s)</span>
          <button type="button" class="tf-btn primary sm" :disabled="tarAberto" @click="abrirTarefa">
            Nova tarefa
          </button>
        </div>
        <div v-if="tarAberto" class="aba-form card-form">
          <input v-model="tarForm.titulo" class="input" placeholder="Título *" />
          <input v-model="tarForm.vencimento" class="input" type="date" />
          <select v-model="tarForm.status" class="input">
            <option value="pendente">Pendente</option>
            <option value="em_andamento">Em andamento</option>
            <option value="concluida">Concluída</option>
          </select>
          <select v-model="tarForm.responsavel" class="input">
            <option value="">Responsável</option>
            <option v-for="u in usuarios" :key="u.id" :value="u.nome">{{ u.nome }}</option>
          </select>
          <textarea v-model="tarForm.descricao" class="input textarea" rows="2" placeholder="Descrição" />
          <div class="inline-add">
            <button type="button" class="tf-btn ghost sm" @click="tarAberto = false">Cancelar</button>
            <button type="button" class="tf-btn primary sm" @click="salvarTarefa">Salvar</button>
          </div>
        </div>
        <div v-if="!form.tarefas.length" class="empty-block"><p>Nenhuma tarefa</p></div>
        <ul v-else class="item-list">
          <li v-for="t in form.tarefas" :key="t.id">
            <div>
              <strong>{{ t.titulo }}</strong>
              <span class="badge">{{ t.status }}</span>
              <p class="empty-hint">{{ t.vencimento }} · {{ t.responsavel || '—' }}</p>
            </div>
            <div class="row-actions">
              <button type="button" class="tf-btn ghost sm" @click="editarTarefa(t)">Editar</button>
              <button type="button" class="tf-btn danger sm" @click="excluirTarefa(t.id)">Excluir</button>
            </div>
          </li>
        </ul>
      </div>

      <!-- NOTAS -->
      <div v-else-if="aba === 'notas'" class="aba-tab">
        <div class="aba-cab">
          <span>{{ form.notas.length }} nota(s)</span>
          <button type="button" class="tf-btn primary sm" :disabled="notaAberto" @click="abrirNota">
            Nova nota
          </button>
        </div>
        <div v-if="notaAberto" class="aba-form card-form">
          <textarea v-model="notaTexto" class="input textarea" rows="3" placeholder="Escreva a nota…" />
          <div class="inline-add">
            <button type="button" class="tf-btn ghost sm" @click="notaAberto = false">Cancelar</button>
            <button type="button" class="tf-btn primary sm" @click="salvarNota">Salvar</button>
          </div>
        </div>
        <div v-if="!form.notas.length" class="empty-block"><p>Nenhuma nota</p></div>
        <ul v-else class="item-list">
          <li v-for="n in form.notas" :key="n.id">
            <div>
              <p>{{ n.texto }}</p>
              <p class="empty-hint">{{ n.autor }} · {{ fmtQuando(n.criadoEm) }}</p>
            </div>
            <button type="button" class="tf-btn danger sm" @click="excluirNota(n.id)">Excluir</button>
          </li>
        </ul>
      </div>

      <!-- INTERAÇÕES -->
      <div v-else-if="aba === 'interacoes'" class="aba-tab">
        <div class="aba-cab">
          <span>{{ form.interacoes.length }} interação(ões)</span>
          <button type="button" class="tf-btn primary sm" :disabled="intAberto" @click="abrirInteracao">
            Nova interação
          </button>
        </div>
        <div v-if="intAberto" class="aba-form card-form">
          <select v-model="intForm.responsavel" class="input">
            <option value="">Responsável</option>
            <option v-for="u in usuarios" :key="u.id" :value="u.nome">{{ u.nome }}</option>
          </select>
          <input v-model="intForm.data" class="input" type="date" />
          <div class="phone-row">
            <input v-model="intForm.hh" class="input" maxlength="2" placeholder="HH" />
            <input v-model="intForm.mm" class="input" maxlength="2" placeholder="MM" />
          </div>
          <textarea v-model="intForm.descricao" class="input textarea" rows="2" placeholder="Descrição *" />
          <div class="inline-add">
            <button type="button" class="tf-btn ghost sm" @click="intAberto = false">Cancelar</button>
            <button type="button" class="tf-btn primary sm" @click="salvarInteracao">Salvar</button>
          </div>
        </div>
        <div v-if="!form.interacoes.length" class="empty-block"><p>Nenhuma interação</p></div>
        <ul v-else class="item-list">
          <li v-for="i in form.interacoes" :key="i.id">
            <div>
              <strong>{{ i.descricao }}</strong>
              <p class="empty-hint">{{ i.data }} {{ i.hora }} · {{ i.responsavel }}</p>
            </div>
            <div class="row-actions">
              <button type="button" class="tf-btn ghost sm" @click="editarInteracao(i)">Editar</button>
              <button type="button" class="tf-btn danger sm" @click="excluirInteracao(i.id)">Excluir</button>
            </div>
          </li>
        </ul>
      </div>

      <!-- TIMELINE -->
      <div v-else-if="aba === 'timeline'" class="aba-tab">
        <div class="tl-filtros">
          <button
            v-for="f in TL_FILTROS"
            :key="f.id"
            type="button"
            class="crm-chip"
            :class="{ 'is-on': timelineFiltro === f.id }"
            @click="timelineFiltro = f.id"
          >
            {{ f.label }}
          </button>
        </div>
        <div v-if="!timelineFiltrada.length" class="empty-block"><p>Timeline vazia</p></div>
        <ul v-else class="item-list">
          <li v-for="t in timelineFiltrada" :key="t.id">
            <div>
              <span class="badge">{{ t.tipo }}</span>
              <strong>{{ t.titulo }}</strong>
              <p>{{ t.detalhe }}</p>
              <p class="empty-hint">{{ fmtQuando(t.em) }}</p>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <footer class="contato-footer">
      <button type="button" class="tf-btn danger" @click="excluir">Excluir</button>
      <button type="button" class="tf-btn primary" :disabled="saving || loading" @click="salvarTudo">
        {{ saving ? 'Salvando…' : 'Concluir' }}
      </button>
    </footer>
  </aside>
</template>

<style scoped>
.contato-panel {
  display: flex;
  flex-direction: column;
  width: min(520px, 100%);
  max-width: 100%;
  height: 100%;
  border-left: 1px solid var(--tf-line, #e2e8f0);
  background: var(--tf-bg, #fff);
  transform-origin: top right;
  min-height: 0;
}
.contato-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 16px 8px;
}
.contato-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}
.contato-sub {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--tf-muted, #64748b);
}
.contato-header-tools {
  display: flex;
  align-items: center;
  gap: 4px;
}
.zoom-lbl {
  font-size: 11px;
  color: var(--tf-muted, #64748b);
  min-width: 36px;
  text-align: center;
}
.contato-tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 0 12px 8px;
  border-bottom: 1px solid var(--tf-line, #e2e8f0);
  scrollbar-width: thin;
}
.contato-tab {
  flex-shrink: 0;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
}
.contato-tab.is-active {
  background: #0f172a;
  color: #fff;
}
.contato-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px 16px 16px;
}
.contato-footer {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--tf-line, #e2e8f0);
}
.aba-form,
.aba-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.input {
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0 10px;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
  background: #fff;
}
.textarea {
  height: auto;
  padding: 8px 10px;
  resize: vertical;
}
.phone-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 8px;
}
.ddi {
  padding-right: 4px;
}
.field-hint,
.empty-hint {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
}
.campo-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}
.campo-switch {
  width: 44px;
  height: 26px;
  border-radius: 999px;
  border: 0;
  background: #e2e8f0;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}
.campo-switch.is-on {
  background: #0f172a;
}
.campo-switch-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s;
}
.campo-switch.is-on .campo-switch-knob {
  transform: translateX(18px);
}
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag-chip {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: #334155;
}
.tag-chip.is-add {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}
.inline-add {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.inline-add .input {
  flex: 1;
  min-width: 120px;
}
.empty-block {
  text-align: center;
  padding: 24px 8px;
  color: #94a3b8;
}
.aba-cab {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
}
.item-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.item-list li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.badge {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}
.row-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.card-form {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
}
.tl-filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.crm-chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.crm-chip.is-on {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}
.file-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.file-list li {
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}
.file-list small {
  display: block;
  color: #94a3b8;
}
</style>
